// Push Notifications JavaScript for FCM token registration
// This file handles Firebase Cloud Messaging setup and token management

class PushNotifications {
  constructor() {
    this.messaging = null;
    this.currentToken = null;
    this.vapidKey = null; // Will be set from Rails credentials or environment
    this.autoPromptShown = false;
    this.isBrave = this.detectBrave();
    this.init();
  }

  async init() {
    console.log('Initializing push notifications...');
    
    if (!('serviceWorker' in navigator)) {
      console.error('Service Worker not supported in this browser');
      return;
    }
    
    if (!('PushManager' in window)) {
      console.error('Push Manager not supported in this browser');
      return;
    }
    
    if (!('Notification' in window)) {
      console.error('Notifications not supported in this browser');
      return;
    }

    try {
      // Import Firebase modules
      console.log('Importing Firebase modules...');
      const { initializeApp } = await import('firebase/app');
      const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

      // Get Firebase config from Rails
      const firebaseConfig = await this.getFirebaseConfig();
      if (!firebaseConfig) {
        console.error('Firebase config not found - cannot initialize FCM');
        return;
      }

      // Initialize Firebase
      console.log('Initializing Firebase app...');
      const app = initializeApp(firebaseConfig);
      this.messaging = getMessaging(app);
      console.log('Firebase messaging initialized');

      // Set up service worker
      await this.setupServiceWorker();

      // Set up foreground message handling
      this.handleForegroundMessage();

      console.log('Push notifications initialized successfully');

    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      console.error('Initialization error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
    }
  }

  async getFirebaseConfig() {
    try {
      console.log('Fetching Firebase config from Rails...');
      // Fetch Firebase config from your Rails app
      const response = await fetch('/firebase_config.json', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      console.log('Firebase config response status:', response.status);
      
      if (response.ok) {
        const config = await response.json();
        console.log('Firebase config received:', {
          hasFirebaseConfig: !!config.firebase,
          hasVapidKey: !!config.vapidKey,
          projectId: config.firebase?.projectId,
          vapidKeyPreview: config.vapidKey ? config.vapidKey.substring(0, 20) + '...' : 'missing'
        });
        this.vapidKey = config.vapidKey;
        
        // Show Brave browser warning if needed
        if (this.isBrave) {
          this.showBraveWarning();
        }
        
        return config.firebase;
      } else {
        console.error('Failed to fetch Firebase config:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Response body:', errorText);
      }
    } catch (error) {
      console.error('Error fetching Firebase config:', error);
      console.error('Network or parsing error details:', {
        name: error.name,
        message: error.message
      });
    }

    // Fallback to environment variables or hardcoded values
    // You should set these in your Rails credentials
    return {
      apiKey: window.FIREBASE_API_KEY,
      authDomain: window.FIREBASE_AUTH_DOMAIN,
      projectId: window.FIREBASE_PROJECT_ID,
      storageBucket: window.FIREBASE_STORAGE_BUCKET,
      messagingSenderId: window.FIREBASE_MESSAGING_SENDER_ID,
      appId: window.FIREBASE_APP_ID
    };
  }

  async setupServiceWorker() {
    try {
      console.log('Setting up service worker for FCM...');
      
      // Check if service worker file is accessible
      const swResponse = await fetch('/firebase-messaging-sw.js');
      console.log('Service worker file accessibility:', swResponse.status);
      
      if (!swResponse.ok) {
        throw new Error(`Service worker file not accessible: ${swResponse.status}`);
      }

      // Register service worker for FCM
      console.log('Registering Firebase messaging service worker...');
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope'
      });

      console.log('Service Worker registration successful:', {
        scope: registration.scope,
        updateViaCache: registration.updateViaCache,
        installing: !!registration.installing,
        waiting: !!registration.waiting,
        active: !!registration.active
      });

      // Wait for service worker to be ready
      const readyRegistration = await navigator.serviceWorker.ready;
      console.log('Service worker is ready');
      
      // Additional check for service worker state
      if (!readyRegistration.active) {
        console.warn('Service worker registered but not active yet, waiting...');
        await new Promise((resolve) => {
          const checkActive = () => {
            if (readyRegistration.active) {
              resolve();
            } else {
              setTimeout(checkActive, 100);
            }
          };
          checkActive();
        });
      }
      
      console.log('Service worker is fully active');

      return registration;
    } catch (error) {
      console.error('Service worker setup failed:', error);
      console.error('Service worker error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw error;
    }
  }

  async requestPermission() {
    console.log('Current notification permission:', Notification.permission);
    
    if (Notification.permission === 'granted') {
      console.log('Notification permission already granted');
      await this.getAndSendToken();
      this.hidePromptBanner();
      this.showSuccessMessage();
      return;
    }
    
    console.log('Requesting notification permission...');
    const permission = await Notification.requestPermission();
    console.log('Permission result:', permission);
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      await this.getAndSendToken();
      this.hidePromptBanner();
      this.showSuccessMessage();
    } else {
      console.log('Notification permission denied or dismissed');
      this.showPermissionDeniedMessage();
    }
  }

  async getAndSendToken() {
    try {
      console.log('Starting FCM token retrieval...');
      console.log('VAPID key:', this.vapidKey);
      console.log('Messaging instance:', this.messaging);
      
      // Check secure context
      if (!window.isSecureContext) {
        throw new Error('Push notifications require a secure context (HTTPS or localhost)');
      }
      
      // Validate VAPID key format
      if (!this.vapidKey || typeof this.vapidKey !== 'string') {
        throw new Error('VAPID key is missing or invalid');
      }
      
      if (!this.vapidKey.startsWith('B') || this.vapidKey.length !== 88) {
        console.warn('VAPID key may be invalid - should start with "B" and be 88 characters long');
        console.warn('Current VAPID key length:', this.vapidKey.length);
        console.warn('Starts with B:', this.vapidKey.startsWith('B'));
      }
      
      const { getToken } = await import('firebase/messaging');
      
      // Check service worker status before getting token
      const registration = await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope');
      if (!registration) {
        throw new Error('Service worker not registered');
      }
      
      if (!registration.active) {
        throw new Error('Service worker not active');
      }
      
      console.log('Attempting to get FCM token with service worker...');
      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: registration
      });

      if (token) {
        console.log('FCM Token successfully retrieved:', token);
        this.currentToken = token;
        await this.sendTokenToServer(token);
      } else {
        console.warn('No registration token available - this could indicate:');
        console.warn('- Service worker registration failed');
        console.warn('- Push messaging not supported');
        console.warn('- User denied notification permission');
        console.warn('- VAPID key is invalid');
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
    // Handle specific error types
    if (error.name === 'AbortError') {
      console.error('AbortError details:');
      console.error('- This usually indicates a service worker registration problem');
      console.error('- Check if firebase-messaging-sw.js is accessible');
      console.error('- Verify VAPID key is correct');
      console.error('- Check browser console for service worker errors');
        
      // Show Brave-specific help if needed
      if (this.isBrave) {
        this.showBraveErrorHelp();
      }
    } else if (error.name === 'NotAllowedError') {
        console.error('NotAllowedError: User denied notification permission');
      } else if (error.name === 'NotSupportedError') {
        console.error('NotSupportedError: Push messaging not supported in this browser');
      }
      
      // Check service worker status
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          console.log('Current service worker registrations:', registrations.length);
          registrations.forEach((registration, index) => {
            console.log(`SW ${index}:`, registration.scope, registration.active?.state);
          });
        });
      }
      
      // Re-throw error so calling code can handle it
      throw error;
    }
  }

  // Detect if the browser is Brave
  detectBrave() {
    return !!(navigator.brave && navigator.brave.isBrave);
  }

  // Show warning for Brave users about Google services requirement
  showBraveWarning() {
    // Only show if we haven't shown it before (use localStorage to persist across page reloads)
    if (localStorage.getItem('braveWarningShownOnce')) {
      return;
    }

    const warningDiv = document.createElement('div');
    warningDiv.className = 'brave-notification-warning';
    warningDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff6b35;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10001;
      max-width: 400px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
    `;

    warningDiv.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 12px;">
        <div style="flex-shrink: 0; font-size: 20px;">🦁</div>
        <div style="flex: 1;">
          <strong>Brave Browser Detected</strong><br>
          To receive push notifications, please enable Google services:
          <br><br>
          <strong>Settings</strong> → <strong>Privacy and security</strong> → <strong>Use Google services for push messaging</strong>
          <br><br>
          <div style="display: flex; gap: 8px; margin-top: 12px;">
            <button onclick="this.parentElement.parentElement.parentElement.parentElement.remove(); localStorage.setItem('braveWarningShownOnce', 'true');" style="
              background: rgba(255,255,255,0.2);
              border: none;
              color: white;
              padding: 6px 12px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 12px;
            ">Got it</button>
            <button onclick="window.open('brave://settings/privacy', '_blank');" style="
              background: rgba(255,255,255,0.9);
              border: none;
              color: #ff6b35;
              padding: 6px 12px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 12px;
              font-weight: 500;
            ">Open Settings</button>
          </div>
        </div>
        <button onclick="this.parentElement.parentElement.remove(); localStorage.setItem('braveWarningShownOnce', 'true');" style="
          background: none;
          border: none;
          color: white;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">&times;</button>
      </div>
    `;

    document.body.appendChild(warningDiv);
  
    // Auto-remove after 15 seconds and mark as shown
    setTimeout(() => {
      if (document.body.contains(warningDiv)) {
        warningDiv.remove();
        localStorage.setItem('braveWarningShownOnce', 'true');
      }
    }, 15000);
  }

  // Show specific error help for Brave users
  showBraveErrorHelp() {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'brave-error-help';
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10001;
      max-width: 420px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
    `;

    errorDiv.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 12px;">
        <div style="flex-shrink: 0; font-size: 20px;">⚠️</div>
        <div style="flex: 1;">
          <strong>Push Notification Setup Failed</strong><br>
          Brave browser requires Google services to be enabled for push notifications.
          <br><br>
          <strong>To fix this:</strong><br>
          1. Go to <strong>brave://settings/privacy</strong><br>
          2. Enable <strong>"Use Google services for push messaging"</strong><br>
          3. Reload this page and try again
          <br><br>
          <div style="display: flex; gap: 8px; margin-top: 12px;">
            <button onclick="window.open('brave://settings/privacy', '_blank');" style="
              background: rgba(255,255,255,0.9);
              border: none;
              color: #dc3545;
              padding: 8px 16px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 12px;
              font-weight: 500;
            ">Open Brave Settings</button>
            <button onclick="window.location.reload();" style="
              background: rgba(255,255,255,0.2);
              border: none;
              color: white;
              padding: 8px 16px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 12px;
            ">Reload Page</button>
          </div>
        </div>
        <button onclick="this.parentElement.parentElement.remove();" style="
          background: none;
          border: none;
          color: white;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">&times;</button>
      </div>
    `;

    document.body.appendChild(errorDiv);
  
    // Auto-remove after 20 seconds
    setTimeout(() => {
      if (document.body.contains(errorDiv)) {
        errorDiv.remove();
      }
    }, 20000);
  }

  async sendTokenToServer(token) {
    try {
      // Get CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      // Detect device information
      const deviceInfo = this.detectDeviceInfo();
      
      const response = await fetch('/api/fcm_tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          fcm_token: token,
          device_name: deviceInfo.deviceName,
          device_type: deviceInfo.deviceType,
          platform: deviceInfo.platform,
          user_agent: navigator.userAgent
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Device registered successfully:', data);
        
        // Store device ID for later deletion
        if (data.data && data.data.device_id) {
          localStorage.setItem('current_device_id', data.data.device_id);
        }
        
        // Reload device list if on settings page
        if (typeof loadDevices === 'function') {
          loadDevices();
        }
      } else {
        console.error('Failed to register device');
      }
    } catch (error) {
      console.error('Error registering device:', error);
    }
  }

  detectDeviceInfo() {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    
    // Detect platform
    let detectedPlatform = 'web';
    if (/android/i.test(ua)) {
      detectedPlatform = 'android';
    } else if (/iphone|ipad|ipod/i.test(ua)) {
      detectedPlatform = 'ios';
    } else if (/mac/i.test(platform)) {
      detectedPlatform = 'macos';
    } else if (/win/i.test(platform)) {
      detectedPlatform = 'windows';
    } else if (/linux/i.test(platform)) {
      detectedPlatform = 'linux';
    }
    
    // Detect device type
    let deviceType = 'desktop';
    if (/mobile|android|iphone|ipod/i.test(ua)) {
      deviceType = 'mobile';
    } else if (/ipad|tablet/i.test(ua)) {
      deviceType = 'tablet';
    }
    
    // Generate device name
    const browserName = this.detectBrowser();
    let deviceName = `${browserName} on ${detectedPlatform.charAt(0).toUpperCase() + detectedPlatform.slice(1)}`;
    
    return {
      deviceName,
      deviceType,
      platform: detectedPlatform
    };
  }

  detectBrowser() {
    const ua = navigator.userAgent;
    if (/brave/i.test(ua) || (navigator.brave && typeof navigator.brave.isBrave === 'function')) {
      return 'Brave';
    } else if (/edg/i.test(ua)) {
      return 'Edge';
    } else if (/chrome/i.test(ua)) {
      return 'Chrome';
    } else if (/safari/i.test(ua)) {
      return 'Safari';
    } else if (/firefox/i.test(ua)) {
      return 'Firefox';
    }
    return 'Browser';
  }

  handleForegroundMessage(payload) {
    const { notification, data } = payload;
    const notificationId = data?.notification_id;
    
    // Check if this notification has already been shown as a popup
    if (notificationId && this.hasBeenShownAsPopup(notificationId)) {
      return; // Already shown, skip
    } else {
      // Show notification if app is in foreground
      if (notification) {
        this.showNotification(notification.title, {
          body: notification.body,
          icon: '/icon-192x192.png', // Add your app icon
          badge: '/badge-72x72.png', // Add your badge icon
          tag: data?.notification_id || 'default',
          data: data,
          actions: [
            {
              action: 'view',
              title: 'View'
            },
            {
              action: 'dismiss',
              title: 'Dismiss'
            }
          ]
        });
      }
      
      // Mark this notification as shown in popup
      if (notificationId) {
        this.markAsShownInPopup(notificationId);
      }
    }

    // Handle data-only messages
    if (data) {
      this.handleNotificationData(data);
    }
  }
  
  // Check if a notification has already been shown as a popup
  hasBeenShownAsPopup(notificationId) {
    try {
      const shownNotifications = JSON.parse(localStorage.getItem('shown_notification_popups') || '[]');
      return shownNotifications.includes(notificationId.toString());
    } catch (e) {
      console.error('Error checking shown notifications:', e);
      return false;
    }
  }
  
  // Mark a notification as shown in popup
  markAsShownInPopup(notificationId) {
    try {
      const shownNotifications = JSON.parse(localStorage.getItem('shown_notification_popups') || '[]');
      
      // Add this notification ID if not already there
      if (!shownNotifications.includes(notificationId.toString())) {
        shownNotifications.push(notificationId.toString());
        
        // Keep only last 1000 notification IDs to prevent localStorage from growing too large
        if (shownNotifications.length > 1000) {
          shownNotifications.shift();
        }
        
        localStorage.setItem('shown_notification_popups', JSON.stringify(shownNotifications));
      }
    } catch (e) {
      console.error('Error marking notification as shown:', e);
    }
  }

  showNotification(title, options) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, options);
      
      notification.onclick = (event) => {
        event.preventDefault();
        
        // Handle notification click
        if (options.data?.url) {
          window.open(options.data.url, '_blank');
        }
        
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  handleNotificationData(data) {
    // Update UI based on notification data
    if (data.type === 'refresh_notifications') {
      this.refreshNotificationCount(data.count);
    } else if (data.contact_id) {
      this.highlightContact(data.contact_id);
    }

    // Trigger custom event for other parts of the app to listen to
    window.dispatchEvent(new CustomEvent('push-notification-received', {
      detail: data
    }));
  }

  refreshNotificationCount(count) {
    const badge = document.querySelector('.notification-badge');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'inline' : 'none';
    }
  }

  highlightContact(contactId) {
    const contactElement = document.querySelector(`[data-contact-id="${contactId}"]`);
    if (contactElement) {
      contactElement.classList.add('highlight-notification');
      setTimeout(() => {
        contactElement.classList.remove('highlight-notification');
      }, 3000);
    }
  }

  // Method to unsubscribe from notifications
  async unsubscribe() {
    try {
      console.log('Starting unsubscribe process...');
      
      // If we have a current token, delete it from Firebase
      if (this.currentToken) {
        console.log('Deleting Firebase token...');
        const { deleteToken } = await import('firebase/messaging');
        await deleteToken(this.messaging);
      } else {
        console.log('No current token in memory, but proceeding with server cleanup...');
      }
      
      // Get the device ID for this browser
      const deviceId = localStorage.getItem('current_device_id');
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      
      let response;
      
      if (deviceId) {
        // Delete specific device
        console.log(`Removing device ${deviceId} from server...`);
        response = await fetch(`/api/fcm_tokens/${deviceId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': csrfToken,
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }
        
        // Clean up stored device ID
        localStorage.removeItem('current_device_id');
      } else {
        // Fallback: disable all devices for this user
        console.log('No device ID found, disabling all devices...');
        response = await fetch('/api/fcm_tokens/disable_all', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok && response.status !== 404) {
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }
      }

      const result = await response.json();
      console.log('Server response:', result);

      this.currentToken = null;
      console.log('Successfully unsubscribed from notifications');
      
      return result;
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      throw error;
    }
  }

  // Check if notifications are supported and enabled
  isSupported() {
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window;
  }

  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }

  // Show automatic prompt after a delay if user hasn't been prompted yet
  async showAutoPromptIfNeeded() {
    if (this.autoPromptShown || Notification.permission !== 'default') {
      return;
    }

    // Check if user has created reminders first
    const mainElement = document.querySelector('main[data-notifications-has-reminders-value]');
    const hasReminders = mainElement ? mainElement.dataset.notificationsHasRemindersValue === 'true' : false;
    
    if (!hasReminders) {
      return;
    }

    // Check if user has been prompted before (stored in localStorage)
    const hasBeenPrompted = localStorage.getItem('push-notifications-prompted');
    if (hasBeenPrompted) {
      return;
    }

    // Wait 10 seconds after page load before showing prompt
    setTimeout(() => {
      this.showPromptBanner();
    }, 10000);
  }

  // Show a friendly banner prompting for notifications
  showPromptBanner() {
    if (this.autoPromptShown || document.getElementById('push-notification-banner')) {
      return;
    }

    // Check if user has reminders before showing banner
    const mainElement = document.querySelector('main[data-notifications-has-reminders-value]');
    const hasReminders = mainElement ? mainElement.dataset.notificationsHasRemindersValue === 'true' : false;
    
    if (!hasReminders) {
      return;
    }

    const banner = document.createElement('div');
    banner.id = 'push-notification-banner';
    banner.innerHTML = `
      <div class="notification-banner">
        <div class="notification-banner__content">
          <div class="notification-banner__icon">🔔</div>
          <div class="notification-banner__text">
            <strong>Stay updated with contact reminders</strong>
            <p>Get notified about important contacts even when the app is closed</p>
            ${this.detectBrave() ? '<p style="color: #e53e3e; font-size: 0.9em;"><strong>⚠️ Brave Browser:</strong> You may need to manually allow notifications in browser settings.</p>' : ''}
          </div>
          <div class="notification-banner__actions">
            <button class="btn-inline btn--md btn-inline--primary" onclick="window.pushNotifications.acceptPrompt()">
              Enable
            </button>
            <button class="btn-inline btn--md btn-inline--secondary" onclick="window.pushNotifications.dismissPrompt()">
              Not now
            </button>
          </div>
        </div>
        <button class="notification-banner__close" onclick="window.pushNotifications.dismissPrompt()" aria-label="Close">
          ×
        </button>
      </div>
      <style>
        .notification-banner {
          position: fixed;
          top: 20px;
          right: 20px;
          max-width: 400px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          border: 1px solid #e2e8f0;
          z-index: 10000;
          animation: slideInRight 0.3s ease-out;
        }
        
        .notification-banner__content {
          display: flex;
          align-items: flex-start;
          padding: 20px;
          gap: 16px;
        }
        
        .notification-banner__icon {
          font-size: 24px;
          line-height: 1;
        }
        
        .notification-banner__text {
          flex: 1;
        }
        
        .notification-banner__text strong {
          display: block;
          color: #2d3748;
          margin-bottom: 4px;
          font-size: 16px;
        }
        
        .notification-banner__text p {
          margin: 0;
          color: #718096;
          font-size: 14px;
          line-height: 1.4;
        }
        
        .notification-banner__actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
        }
        
        .notification-banner__close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: none;
          border: none;
          font-size: 20px;
          color: #a0aec0;
          cursor: pointer;
          line-height: 1;
          padding: 4px;
        }
        
        .notification-banner__close:hover {
          color: #718096;
        }
        
        .btn-inline { display: inline-flex; align-items: center; font-weight: 500; border: 1px solid transparent; border-radius: 6px; text-decoration: none; cursor: pointer; transition: all 0.2s; }
        .btn--md { padding: 8px 16px; font-size: 14px; }
        .btn-inline--primary { background: #667eea; color: white; border-color: #667eea; }
        .btn-inline--primary:hover { background: #5a67d8; }
        .btn-inline--secondary { background: transparent; color: #667eea; border-color: #e2e8f0; }
        .btn-inline--secondary:hover { background: #f7fafc; }
        
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @media (max-width: 600px) {
          .notification-banner {
            top: 10px;
            right: 10px;
            left: 10px;
            max-width: none;
          }
          
          .notification-banner__content {
            padding: 16px;
          }
          
          .notification-banner__actions {
            flex-direction: column;
          }
          
          .btn-inline {
            width: 100%;
          }
        }
      </style>
    `;

    document.body.appendChild(banner);
    this.autoPromptShown = true;
  }

  // Handle user accepting the prompt
  async acceptPrompt() {
    localStorage.setItem('push-notifications-prompted', 'true');
    this.hidePromptBanner();
    await this.requestPermission();
  }

  // Handle user dismissing the prompt
  dismissPrompt() {
    localStorage.setItem('push-notifications-prompted', 'true');
    this.hidePromptBanner();
  }

  // Hide the prompt banner
  hidePromptBanner() {
    const banner = document.getElementById('push-notification-banner');
    if (banner) {
      banner.style.animation = 'slideOutRight 0.3s ease-in forwards';
      setTimeout(() => {
        banner.remove();
      }, 300);
    }
  }

  

  // Show success message when notifications are enabled
  showSuccessMessage() {
    this.showToast('✅ Notifications enabled! You\'ll now receive contact reminders.', 'success');
  }

  // Show message when permission is denied
  showPermissionDeniedMessage() {
    this.showToast('⚠️ Notifications blocked. You can enable them later in settings.', 'warning');
  }

  // Show a temporary toast message
  showToast(message, type = 'info') {
    const existingToast = document.getElementById('push-notification-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.id = 'push-notification-toast';
    toast.innerHTML = `
      <div class="notification-toast notification-toast--${type}">
        ${message}
      </div>
      <style>
        .notification-toast {
          position: fixed;
          bottom: 20px;
          right: 20px;
          max-width: 350px;
          padding: 16px 20px;
          border-radius: 8px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          z-index: 10000;
          animation: slideInUp 0.3s ease-out;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        
        .notification-toast--success {
          background: #38a169;
        }
        
        .notification-toast--warning {
          background: #ed8936;
        }
        
        .notification-toast--info {
          background: #3182ce;
        }
        
        @keyframes slideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @media (max-width: 600px) {
          .notification-toast {
            bottom: 10px;
            right: 10px;
            left: 10px;
            max-width: none;
          }
        }
      </style>
    `;

    document.body.appendChild(toast);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.style.animation = 'slideInUp 0.3s ease-in reverse';
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, 5000);
  }

  // Method to manually trigger the prompt (for use in settings)
  showManualPrompt() {
    if (Notification.permission === 'granted') {
      this.showToast('✅ Notifications are already enabled!', 'success');
      return;
    }

    if (Notification.permission === 'denied') {
      this.showToast('⚠️ Notifications are blocked. Please enable them in your browser settings.', 'warning');
      return;
    }

    // Reset the prompt flag and show banner
    this.autoPromptShown = false;
    this.showPromptBanner();
  }

  // Check permission status for external components
  getPermissionInfo() {
    return {
      isSupported: this.isSupported(),
      permission: this.isSupported() ? Notification.permission : 'unsupported',
      hasToken: !!this.currentToken
    };
  }
}

// Initialize push notifications when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  if ('serviceWorker' in navigator) {
    window.pushNotifications = new PushNotifications();
  }
});

// Export for use in other modules
export default PushNotifications;